/*
 * Decodes a picture upright for the client-side resize (#100). The canvas it is drawn on carries no
 * EXIF, so a phone photo's Orientation tag has to be applied while decoding, or the converted file
 * is stored on its side. `imageOrientation: 'from-image'` does that, but Safari 15, Chrome 111 and
 * Firefox 110 and older reject the value with a TypeError, which used to switch the whole feature
 * off for them (#114). This module finds another way to an upright bitmap before giving up:
 *
 *   1. 'from-image'            the option is accepted: every browser from Safari 16, Chrome 112, Firefox 111.
 *   2. 'untagged'              the option is rejected, but the JPEG's own header says Orientation 1 (or
 *                              carries no tag), so there is nothing to apply: decode with no options.
 *   3. 'default-orientation'   the option is rejected and the picture may be turned. Once per page, a
 *                              195-byte JPEG tagged Orientation 6 is decoded with no options: a browser
 *                              that applies the tag by itself returns it 1x2, not 2x1. If it does, the
 *                              picture is decoded the same way; if not, the caller sends the original.
 *
 * No user-agent sniffing: the probe asks the decoder itself. The caller sees which path ran.
 */

export type ImageDecoder = (source: Blob, options?: ImageBitmapOptions) => Promise<ImageBitmap>;

export type DecodePath = 'from-image' | 'untagged' | 'default-orientation';

// A 2x1 grey baseline JPEG with EXIF Orientation 6 (rotate 90 degrees clockwise to display).
// Shown upright it is 1 wide and 2 tall. Built with Pillow 12.3; `jpegOrientation` reads 6 from it.
export const PROBE_JPEG = Uint8Array.from([
  0xff, 0xd8, 0xff, 0xe0, 0x00, 0x10, 0x4a, 0x46, 0x49, 0x46, 0x00, 0x01, 0x01, 0x00, 0x00, 0x01, 0x00, 0x01, 0x00,
  0x00, 0xff, 0xe1, 0x00, 0x22, 0x45, 0x78, 0x69, 0x66, 0x00, 0x00, 0x4d, 0x4d, 0x00, 0x2a, 0x00, 0x00, 0x00, 0x08,
  0x00, 0x01, 0x01, 0x12, 0x00, 0x03, 0x00, 0x00, 0x00, 0x01, 0x00, 0x06, 0x00, 0x00, 0x00, 0x00, 0x00, 0x00, 0xff,
  0xdb, 0x00, 0x43, 0x00, 0x10, 0x0b, 0x0c, 0x0e, 0x0c, 0x0a, 0x10, 0x0e, 0x0d, 0x0e, 0x12, 0x11, 0x10, 0x13, 0x18,
  0x28, 0x1a, 0x18, 0x16, 0x16, 0x18, 0x31, 0x23, 0x25, 0x1d, 0x28, 0x3a, 0x33, 0x3d, 0x3c, 0x39, 0x33, 0x38, 0x37,
  0x40, 0x48, 0x5c, 0x4e, 0x40, 0x44, 0x57, 0x45, 0x37, 0x38, 0x50, 0x6d, 0x51, 0x57, 0x5f, 0x62, 0x67, 0x68, 0x67,
  0x3e, 0x4d, 0x71, 0x79, 0x70, 0x64, 0x78, 0x5c, 0x65, 0x67, 0x63, 0xff, 0xc0, 0x00, 0x0b, 0x08, 0x00, 0x01, 0x00,
  0x02, 0x01, 0x01, 0x11, 0x00, 0xff, 0xc4, 0x00, 0x14, 0x00, 0x01, 0x00, 0x00, 0x00, 0x00, 0x00, 0x00, 0x00, 0x00,
  0x00, 0x00, 0x00, 0x00, 0x00, 0x00, 0x00, 0x00, 0xff, 0xc4, 0x00, 0x14, 0x10, 0x01, 0x00, 0x00, 0x00, 0x00, 0x00,
  0x00, 0x00, 0x00, 0x00, 0x00, 0x00, 0x00, 0x00, 0x00, 0x00, 0x00, 0xff, 0xda, 0x00, 0x08, 0x01, 0x01, 0x00, 0x00,
  0x3f, 0x00, 0x3f, 0xff, 0xd9,
]);
export const PROBE_UPRIGHT = { width: 1, height: 2 };

// TypeScript 4.9's DOM types predate the value.
const FROM_IMAGE = { imageOrientation: 'from-image' } as unknown as ImageBitmapOptions;

export const NO_UPRIGHT_PATH_MESSAGE =
  "this browser rejects imageOrientation 'from-image' and does not apply the EXIF Orientation tag by itself";

let defaultOrientationProbe: Promise<boolean> | undefined;

// For tests: the probe result is kept for the page's lifetime.
export function resetOrientationProbe(): void {
  defaultOrientationProbe = undefined;
}

/*
 * Whether a decode with no options applies the EXIF Orientation tag, asked once per page. A probe
 * that can't be decoded at all counts as no, so the caller falls back to the original file.
 */
export function defaultDecodeIsUpright(decoder: ImageDecoder): Promise<boolean> {
  if (!defaultOrientationProbe) {
    defaultOrientationProbe = (async () => {
      let bitmap: ImageBitmap | undefined;
      try {
        bitmap = await decoder(new Blob([PROBE_JPEG], { type: 'image/jpeg' }));
        return bitmap.width === PROBE_UPRIGHT.width && bitmap.height === PROBE_UPRIGHT.height;
      } catch {
        return false;
      } finally {
        bitmap?.close();
      }
    })();
  }
  return defaultOrientationProbe;
}

/*
 * The picture as an upright bitmap, and the path that produced it. `orientation` is the JPEG header's
 * Orientation tag (1 when absent), or null when the file is not a JPEG or the tag could not be read.
 * Throws when no path gives an upright bitmap, or when the picture itself can't be decoded.
 */
export async function decodeUpright(
  file: Blob,
  orientation: number | null,
  decoder: ImageDecoder
): Promise<{ bitmap: ImageBitmap; path: DecodePath }> {
  try {
    return { bitmap: await decoder(file, FROM_IMAGE), path: 'from-image' };
  } catch (error) {
    // WebIDL rejects an unknown enum value with a TypeError before the image is looked at.
    // A picture that can't be decoded is a DOMException, and stays the caller's failure.
    if (!(error instanceof TypeError)) throw error;
  }
  if (orientation === 1) {
    return { bitmap: await decoder(file), path: 'untagged' };
  }
  if (!(await defaultDecodeIsUpright(decoder))) {
    throw new Error(NO_UPRIGHT_PATH_MESSAGE);
  }
  return { bitmap: await decoder(file), path: 'default-orientation' };
}
